import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './Firebase';
import './Connexion.css';
import logo from './safi1.jpeg';
import safi10 from './safi10.webp';

export default function Connexion() {
  const navigate = useNavigate();
  const [form, setForm]       = useState({ mail: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState('');

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.mail.trim() || !/\S+@\S+\.\S+/.test(form.mail)) e.mail = 'Adresse e-mail invalide.';
    if (!form.password) e.password = 'Le mot de passe est requis.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setFirebaseError('');
    try {
      // 1. Connexion Firebase Auth
      const { user } = await signInWithEmailAndPassword(auth, form.mail, form.password);

      // 2. Récupérer les infos Firestore (niveau, rôle...)
      const snap = await getDoc(doc(db, 'etudiants', user.uid));
      if (snap.exists()) {
        const data = snap.data();
        // Vérifier si le compte est actif
        if (!data.actif) {
          await auth.signOut();
          setFirebaseError('Votre compte est en attente de confirmation de paiement. Notre équipe va l\'activer sous peu.');
          setLoading(false);
          return;
        }
        if (data.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const msgs = {
        'auth/user-not-found':        'Aucun compte associé à cet e-mail.',
        'auth/wrong-password':        'Mot de passe incorrect.',
        'auth/invalid-credential':    'Identifiants incorrects.',
        'auth/too-many-requests':     'Trop de tentatives. Réessayez plus tard.',
        'auth/network-request-failed':'Vérifiez votre connexion internet.',
        'auth/invalid-email':         'Adresse e-mail invalide.',
      };
      setFirebaseError(msgs[err.code] || 'Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // Connexion au Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="connexion">

      {/* GAUCHE */}
      <div className="connexion__left">
        <img src={safi10} alt="SAFI Academia" className="connexion__bg" />
        <div className="connexion__overlay" />
        <div className="connexion__left-content">
          <Link to="/" className="connexion__logo-link">
            <img src={logo} alt="SAFI Academia" className="connexion__logo" />
          </Link>
          <h1 className="connexion__left-title">Bon retour<br />parmi nous.</h1>
          <p className="connexion__left-sub">
            Connectez-vous à votre espace SAFI Academia pour accéder à vos cours, suivre votre progression et continuer votre aventure.
          </p>
          <p className="connexion__left-new">
            Pas encore de compte ?{' '}
            <Link to="/postuler" className="connexion__left-link">Postuler maintenant</Link>
          </p>
        </div>
      </div>

      {/* DROITE */}
      <div className="connexion__right">
        <div className="connexion__form-wrap">
          <Link to="/" className="connexion__back">← Retour à l'accueil</Link>

          <div className="connexion__header">
            <h2 className="connexion__title">Se connecter</h2>
            <p className="connexion__sub">Entrez vos identifiants pour accéder à votre espace.</p>
          </div>

          {/* Erreur Firebase */}
          {firebaseError && (
            <div className="firebase-error">{firebaseError}</div>
          )}

          <form className="connexion__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

            {/* Mail */}
            <div className="field">
              <label>Adresse e-mail</label>
              <div className="input-icon">
                <Mail size={16} className="input-icon__icon" />
                <input
                  type="email"
                  placeholder="exemple@email.com"
                  value={form.mail}
                  onChange={set('mail')}
                  onKeyDown={handleKeyDown}
                  className={errors.mail ? 'input--error' : ''}
                />
              </div>
              {errors.mail && <span className="field__error">{errors.mail}</span>}
            </div>

            {/* Mot de passe */}
            <div className="field">
              <div className="field__label-row">
                <label>Mot de passe</label>
                <a href="#" className="field__forgot">Mot de passe oublié ?</a>
              </div>
              <div className="input-icon input-pwd">
                <Lock size={16} className="input-icon__icon" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Votre mot de passe"
                  value={form.password}
                  onChange={set('password')}
                  onKeyDown={handleKeyDown}
                  className={errors.password ? 'input--error' : ''}
                />
                <button type="button" className="input-pwd__toggle" onClick={() => setShowPwd(!showPwd)}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <span className="field__error">{errors.password}</span>}
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : <>Se connecter <ArrowRight size={16} /></>}
            </button>

            <p className="connexion__register">
              Pas encore inscrit ?{' '}
              <Link to="/postuler">Créer un compte</Link>
            </p>

          </form>
        </div>
      </div>
    </div>
  );
}